import { useToastError } from "@/components/ui/use-toast";
import { useApi } from "@/hooks/useApi";
import { TeamUserDTO } from "@shaarp/common";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

type UsersContextType = {
  users: TeamUserDTO[];
  apiFetchUsers: (options?: { force?: boolean }) => Promise<void>;
  apiFetchLoading: boolean;
};

const UsersContext = createContext<UsersContextType>({
  users: [],
  apiFetchUsers: async () => {},
  apiFetchLoading: false,
});

export const useUsers = (): UsersContextType => {
  const context = useContext(UsersContext);
  if (!context) {
    throw new Error("useUsers must be used within an UsersProvider");
  }
  return context;
};

export const UsersProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const toastError = useToastError();
  const api = useApi();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [users, setUsers] = useState<TeamUserDTO[]>([]);

  // Persist the in-flight promise across renders so concurrent callers share it
  const fetchUsersPromiseRef = useRef<Promise<void> | null>(null);
  // Refs mirroring users.length / isLoading so fetchUsers identity stays stable
  // across fetch cycles — otherwise table-cell useEffect([apiFetchUsers]) loops.
  const usersLengthRef = useRef(0);
  const isLoadingRef = useRef(false);

  useEffect(() => {
    usersLengthRef.current = users.length;
  }, [users]);
  useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);

  const fetchUsers = useCallback(
    async ({ force = false }: { force?: boolean } = {}) => {
      if ((!force && usersLengthRef.current) || isLoadingRef.current) {
        return;
      }

      if (fetchUsersPromiseRef.current) {
        await fetchUsersPromiseRef.current;
        return;
      }

      fetchUsersPromiseRef.current = (async () => {
        try {
          setIsLoading(true);

          const { data } = await api.get<TeamUserDTO[]>(`/my/team/users`);
          setUsers(data);
        } catch (error) {
          toastError();
        } finally {
          setIsLoading(false);
          fetchUsersPromiseRef.current = null;
        }
      })();

      await fetchUsersPromiseRef.current;
    },
    [api, toastError],
  );

  const value = useMemo(
    () => ({
      users,
      apiFetchUsers: fetchUsers,
      apiFetchLoading: isLoading,
    }),
    [users, fetchUsers, isLoading],
  );

  return (
    <UsersContext.Provider value={value}>{children}</UsersContext.Provider>
  );
};
